import { create } from 'zustand';

export type FormTab = {
  formId: string;
  formName: string;
  isActive: boolean;
  openedAt: string;
};

interface FormTabStore {
  tabs: FormTab[];
  activeTabId: string | null;
  setTabs: (tabs: FormTab[]) => void;
  addFormTab: (tab: Omit<FormTab, 'isActive' | 'openedAt'>) => void;
  removeFormTab: (formId: string) => void;
  setActiveTab: (formId: string) => void;
  getActiveTab: () => FormTab | null;
  getPreviousTab: (currentFormId: string) => FormTab | null;
  getNextTab: (currentFormId: string) => FormTab | null;
  removeFormTabWithNavigation: (formId: string, navigate: (path: string) => void) => void;
  isTabOpen: (formId: string) => boolean;
  clearAllTabs: () => void;
}

export const useFormTabStore = create<FormTabStore>((set, get) => ({
  tabs: [],
  activeTabId: null,

  setTabs: (tabs: FormTab[]) => set({ tabs }),

  addFormTab: (tab: Omit<FormTab, 'isActive' | 'openedAt'>) => {
    const state = get();
    const existingTab = state.tabs.find(t => t.formId === tab.formId);
    
    if (existingTab) {
      set({
        tabs: state.tabs.map(t => ({
          ...t,
          isActive: t.formId === tab.formId
        })),
        activeTabId: tab.formId
      });
      return;
    }

    const updatedTabs = state.tabs.map(t => ({ ...t, isActive: false }));
    
    const newTab: FormTab = {
      ...tab,
      isActive: true,
      openedAt: new Date().toISOString()
    };

    set({
      tabs: [newTab, ...updatedTabs],
      activeTabId: tab.formId
    });
  },

  removeFormTab: (formId: string) => {
    const state = get();
    const tabToRemove = state.tabs.find(t => t.formId === formId);
    
    if (!tabToRemove) return;

    const remainingTabs = state.tabs.filter(t => t.formId !== formId);
    
    let newActiveTabId = state.activeTabId;
    if (state.activeTabId === formId) {
      if (remainingTabs.length > 0) {
        newActiveTabId = remainingTabs[0].formId;
        remainingTabs[0].isActive = true;
      } else {
        newActiveTabId = null;
      }
    }

    set({
      tabs: remainingTabs,
      activeTabId: newActiveTabId
    });
  },

  setActiveTab: (formId: string) => {
    const state = get();
    const tabExists = state.tabs.find(tab => tab.formId === formId);
    
    if (!tabExists) {
      console.warn(`Tab with formId ${formId} not found`);
      return;
    }
    
    const updatedTabs = state.tabs.map(tab => ({
      ...tab,
      isActive: tab.formId === formId
    }));

    set({
      tabs: updatedTabs,
      activeTabId: formId
    });
  },

  getActiveTab: () => {
    const state = get();
    return state.tabs.find(tab => tab.isActive) || null;
  },

  getPreviousTab: (currentFormId: string) => {
    const state = get();
    const currentIndex = state.tabs.findIndex(tab => tab.formId === currentFormId);
    
    if (currentIndex === -1) {
      return null;
    }
    
    if (currentIndex === 0) {
      return null;
    }

    return state.tabs[currentIndex - 1];
  },

  getNextTab: (currentFormId: string) => {
    const state = get();
    const currentIndex = state.tabs.findIndex(tab => tab.formId === currentFormId);
    
    if (currentIndex === -1) {
      return null;
    }
    
    if (currentIndex === state.tabs.length - 1) {
      return null;
    }

    return state.tabs[currentIndex + 1];
  },

  removeFormTabWithNavigation: (formId: string, navigate: (path: string) => void) => {
    const state = get();
    const tabToRemove = state.tabs.find(t => t.formId === formId);
    
    if (!tabToRemove) return;

    // Verifica se é a primeira tab (índice 0)
    const currentIndex = state.tabs.findIndex(tab => tab.formId === formId);
    const isFirstTab = currentIndex === 0;
    
    let targetTab = null;
    
    if (isFirstTab) {
      // Se é a primeira tab, tenta pegar a próxima tab
      targetTab = get().getNextTab(formId);
    } else {
      // Se não é a primeira tab, pega a tab anterior
      targetTab = get().getPreviousTab(formId);
    }
    
    // Remove a tab
    get().removeFormTab(formId);
    
    // Navegação baseada na lógica
    if (targetTab) {
      get().setActiveTab(targetTab.formId);
      navigate(`/v2/form/${targetTab.formId}`);
    } else {
      // Se não há tab para navegar, vai para home
      navigate('/');
    }
  },

  isTabOpen: (formId: string) => {
    const state = get();
    return state.tabs.some(tab => tab.formId === formId);
  },

  clearAllTabs: () => {
    set({
      tabs: [],
      activeTabId: null
    });
  }
}));
